// src/components/singalong/exportSingAlongVideo.js
//
// Records a sing-along to a video file in the browser.
//
// HOW
// ---
// The song plays (silently, unless you ask to hear it) through the Web Audio
// API, the lyrics are drawn onto a hidden canvas every frame, and the browser's
// MediaRecorder captures both into one file. Recording happens in REAL TIME:
// a 3-minute song takes 3 minutes. The tab must stay visible the whole time —
// browsers pause drawing in hidden tabs, and the video would freeze.
//
// MP4 OR WEBM
// -----------
// Current Chrome, Edge and Safari record MP4 (H.264 + AAC) directly. Older
// browsers and Firefox only record WebM; the result says which one you got.
// Whichever it is, it has been test-played first — see "WHICH FORMAT" below.
//
// WHY THE SONG IS DOWNLOADED FIRST
// --------------------------------
// The browser will only let a page record audio from another website if that
// website says it may (CORS). Firebase Storage says no until the bucket is
// given a CORS setting. Without it, fetch() fails here and we report
// "cors" so the studio can show the one-time fix. See SINGALONG-SETUP.md.

import { renderSingAlongFrame } from "./renderSingAlongFrame";
import { fadeLevelAt, fileFadesItself, logoUrlFor, stopTimeOf } from "../../services/lyricSyncService";

/**
 * Loads the logo so the canvas can draw it AND still be recorded: needs
 * crossOrigin="anonymous" plus a bucket CORS rule allowing this site (your
 * SBL cors.json already lists the portal's addresses). Resolves null instead
 * of failing, so a missing logo never stops a video.
 */
const loadLogo = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timer = setTimeout(() => resolve(null), 8000);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });

// WHICH FORMAT — AND WHY "SUPPORTED" IS NOT ENOUGH
// ------------------------------------------------
// MediaRecorder.isTypeSupported() answers "can I RECORD this?", not "can
// anything PLAY what I record?". Those come apart: a browser can encode H.264
// and still be unable to decode it, or accept a profile its encoder then
// botches. The file records, uploads and saves without a single error, and
// every <video> that opens it shows a grey box. That is exactly what happened
// after the list below was reordered to put H.264 High profile first.
//
// So the format is no longer taken on trust:
//   1. Before recording, each candidate the browser claims to support gets a
//      ~1 second test recording at the real size, with sound, and that test
//      file is opened in a <video>. The first one that actually shows a
//      picture is used. The answer is remembered for the session.
//   2. After recording, the finished file is opened the same way before it is
//      offered for preview or upload. If it won't play, that format is struck
//      off and the studio says so, instead of saving a grey video to SBL.
//
// Order: H.264 Constrained Baseline first — the profile every phone, smart
// board, QuickTime and PowerPoint can play — then Main, then High. A bare
// "video/mp4" is still not listed: browsers without H.264 accept it and put
// VP9 inside an .mp4, which plays in Chrome and fails nearly everywhere else.
// Without a working H.264 we make WebM and say so.
const MIME_CANDIDATES = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1.4D401F,mp4a.40.2",
  "video/mp4;codecs=avc1.640028,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a",
  "video/mp4;codecs=avc1.42E01E,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

/* ---- per-session memory of what works and what doesn't ---- */

const STORE_KEY = "sbl.videoMime.v2";

const readStore = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

const writeStore = (patch) => {
  try {
    window.sessionStorage.setItem(STORE_KEY, JSON.stringify({ ...readStore(), ...patch }));
  } catch {
    /* private mode etc. — memory only */
  }
};

const memory = (() => {
  const s = readStore();
  return { good: s.good || {}, bad: new Set(s.bad || []) };
})();

const sizeKey = (w, h) => `${w}x${h}`;

const markBad = (mime) => {
  memory.bad.add(mime);
  Object.keys(memory.good).forEach((k) => {
    if (memory.good[k] === mime) delete memory.good[k];
  });
  writeStore({ good: memory.good, bad: Array.from(memory.bad) });
};

const markGood = (mime, w, h) => {
  memory.good[sizeKey(w, h)] = mime;
  writeStore({ good: memory.good, bad: Array.from(memory.bad) });
};

const claimed = () =>
  typeof MediaRecorder === "undefined"
    ? []
    : MIME_CANDIDATES.filter((m) => !memory.bad.has(m) && MediaRecorder.isTypeSupported(m));

/**
 * Best guess, instantly — for labels like "as an MP4 file". The format that
 * passed the playback test if one has, otherwise the first one the browser
 * claims. The real choice is made by findPlayableMime() when recording starts.
 */
export const pickVideoMime = () => {
  const tested = Object.values(memory.good)[0];
  return tested || claimed()[0] || null;
};

export const canExportVideo = () =>
  typeof MediaRecorder !== "undefined" &&
  typeof HTMLCanvasElement !== "undefined" &&
  typeof HTMLCanvasElement.prototype.captureStream === "function" &&
  Boolean(pickVideoMime());

export class SingAlongExportError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

/**
 * Opens a recorded file in an off-screen <video> and resolves
 * { ok, why }. ok means the browser decoded a picture from it — the thing a
 * grey video fails to do.
 */
export const checkPlayable = (blob, timeoutMs = 10000) =>
  new Promise((resolve) => {
    if (!blob || !blob.size) {
      resolve({ ok: false, why: "the file is empty" });
      return;
    }
    const url = URL.createObjectURL(blob);
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    let settled = false;
    const finish = (ok, why = "") => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      v.onerror = null;
      v.onloadeddata = null;
      try {
        v.removeAttribute("src");
        v.load();
      } catch {
        /* nothing to release */
      }
      URL.revokeObjectURL(url);
      resolve({ ok, why });
    };
    const timer = setTimeout(() => finish(false, "it never showed a picture"), timeoutMs);
    v.onerror = () => {
      const e = v.error;
      finish(false, e ? `the browser refused it (error ${e.code}${e.message ? `: ${e.message}` : ""})` : "the browser refused it");
    };
    // loadeddata = the first frame has been decoded.
    v.onloadeddata = () => {
      if (v.videoWidth > 0 && v.videoHeight > 0) finish(true);
      else finish(false, "it has sound but no picture");
    };
    v.src = url;
  });

/** Records ~1 s of a moving test card, with a tone, in one format. */
const testRecording = async (mime, width, height) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  const stream = canvas.captureStream(30);

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const actx = new AudioCtx();
  try {
    await actx.resume();
  } catch {
    /* recorded silent; still a valid test of the picture */
  }
  const osc = actx.createOscillator();
  const dest = actx.createMediaStreamDestination();
  osc.connect(dest); // never to the speakers
  osc.start();
  dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));

  let recorder;
  try {
    recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2_000_000 });
  } catch (err) {
    stream.getTracks().forEach((t) => t.stop());
    osc.stop();
    actx.close().catch(() => {});
    return null;
  }

  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size) chunks.push(e.data);
  };
  const stopped = new Promise((resolve) => {
    recorder.onstop = resolve;
    recorder.onerror = resolve;
  });

  let raf = 0;
  const started = performance.now();
  const draw = () => {
    const k = ((performance.now() - started) / 1000) % 1;
    ctx.fillStyle = `hsl(${Math.round(k * 360)}, 70%, 50%)`;
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = "#fff";
    ctx.fillRect(k * (width - 80), height / 2 - 40, 80, 80);
    raf = requestAnimationFrame(draw);
  };
  draw();
  recorder.start(250);
  await new Promise((r) => setTimeout(r, 1200));
  if (recorder.state !== "inactive") recorder.stop();
  await stopped;
  cancelAnimationFrame(raf);
  stream.getTracks().forEach((t) => t.stop());
  osc.stop();
  actx.close().catch(() => {});

  return new Blob(chunks, { type: mime.split(";")[0] });
};

/**
 * The first format whose test recording actually plays back in this browser,
 * at this size. Remembered for the session, so this runs once.
 */
export const findPlayableMime = async ({ width = 1280, height = 720, onStatus } = {}) => {
  const known = memory.good[sizeKey(width, height)];
  if (known && !memory.bad.has(known)) return known;

  const tried = [];
  for (const mime of claimed()) {
    if (onStatus) onStatus(`Checking this browser can play ${labelOf(mime)}…`);
    // eslint-disable-next-line no-await-in-loop
    const blob = await testRecording(mime, width, height);
    // eslint-disable-next-line no-await-in-loop
    const check = blob ? await checkPlayable(blob, 6000) : { ok: false, why: "it would not start recording" };
    if (check.ok) {
      markGood(mime, width, height);
      return mime;
    }
    tried.push(`${labelOf(mime)} — ${check.why}`);
    markBad(mime);
  }
  throw new SingAlongExportError(
    "unsupported",
    `This browser can record video but can't play back any format it records${
      tried.length ? ` (${tried.join("; ")})` : ""
    }. Use the latest Chrome or Edge on a computer.`
  );
};

/** "MP4 (H.264 + AAC)" etc., for messages. */
export const labelOf = (mime = "") => {
  const m = String(mime).toLowerCase();
  const box = m.startsWith("video/mp4") ? "MP4" : m.startsWith("video/webm") ? "WebM" : "video";
  const v = /avc1/.test(m) ? "H.264" : /vp9/.test(m) ? "VP9" : /vp8/.test(m) ? "VP8" : "";
  const a = /mp4a/.test(m) ? "AAC" : /opus/.test(m) ? "Opus" : "";
  return [box, v || a ? `(${[v, a].filter(Boolean).join(" + ")})` : ""].filter(Boolean).join(" ");
};

/**
 * @returns {Promise<{ blob, mime, ext, seconds, logoMissing }>}
 *
 * opts: {
 *   musicUrl, parsed, cues, themeKey, title, logoOpacity, logoUrl?,
 *   width = 1280, height = 720, fps = 30,
 *   audible = false,            // hear the song while it records
 *   endAt = null,               // "End sing-along here": fade out over
 *                               // FADE_SECONDS from this time, then stop
 *   renderFrame,                // optional: draw something other than the
 *                               // sing-along screen (the lesson videos use
 *                               // this). Called as renderFrame(ctx,
 *                               // { width, height, t, fade, logo }).
 *   onProgress(fraction, seconds, total),
 *   onStatus(text),             // optional: "Checking this browser…" notes
 *                               // ("" clears)
 *   signal                      // AbortSignal: stop early, no file
 * }
 *
 * Returns also { codec, codecLabel } — the format really used.
 */
export const exportSingAlongVideo = async (opts) => {
  const {
    musicUrl,
    parsed,
    cues,
    themeKey,
    title,
    logoOpacity = 0,
    logoUrl,
    width = 1280,
    height = 720,
    fps = 30,
    audible = false,
    endAt = null,
    renderFrame = null,
    onProgress,
    onStatus,
    signal,
  } = opts;

  if (!canExportVideo()) {
    throw new SingAlongExportError(
      "unsupported",
      "This browser cannot record video. Use the latest Chrome or Edge on a computer."
    );
  }

  // 0. A format this browser can actually PLAY back (see the note at the top).
  const mime = await findPlayableMime({ width, height, onStatus });
  if (signal?.aborted) throw new SingAlongExportError("aborted", "Recording stopped.");
  if (onStatus) onStatus("");

  // 1. Download the song (needs CORS on the storage bucket).
  let audioBlob;
  try {
    const res = await fetch(musicUrl, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    audioBlob = await res.blob();
  } catch (err) {
    throw new SingAlongExportError(
      "cors",
      "The song could not be downloaded for recording. The storage bucket needs a one-time CORS setting."
    );
  }

  // A custom renderer (lesson video) always wants the logo for the shirts.
  const wantLogo = renderFrame ? Boolean(logoUrl) : logoOpacity > 0;
  const logo = wantLogo ? await loadLogo(logoUrl || logoUrlFor(themeKey)) : null;
  const logoMissing = wantLogo && !logo;

  const audioUrl = URL.createObjectURL(audioBlob);
  const audio = new Audio();
  audio.preload = "auto";
  audio.src = audioUrl;

  await new Promise((resolve, reject) => {
    const ok = () => resolve();
    const bad = () => reject(new SingAlongExportError("audio", "The song file could not be read."));
    audio.addEventListener("canplaythrough", ok, { once: true });
    audio.addEventListener("error", bad, { once: true });
    audio.load();
  });

  // 2. Wire audio -> recorder (and optionally -> speakers).
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const actx = new AudioCtx();
  await actx.resume();
  const source = actx.createMediaElementSource(audio);
  const dest = actx.createMediaStreamDestination();
  // song -> fade gain -> recorder (and speakers). The gain only moves after
  // the end marker; before that it is a plain pass-through.
  const gain = actx.createGain();
  gain.gain.value = 1;
  source.connect(gain);
  gain.connect(dest);
  if (audible) gain.connect(actx.destination);

  // Where the video stops: end marker + fade, or the song's own end.
  const songLength = Number.isFinite(audio.duration) ? audio.duration : 0;
  const stopAt0 = stopTimeOf(endAt);
  const stopAt = stopAt0 !== null && (!songLength || endAt < songLength) ? stopAt0 : null;
  const endMark = stopAt !== null ? endAt : null;

  // 3. Canvas -> video track.
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  const draw = () =>
    renderFrame
      ? renderFrame(ctx, {
          width,
          height,
          t: audio.currentTime,
          fade: fadeLevelAt(endMark, audio.currentTime),
          logo,
        })
      : renderSingAlongFrame(ctx, {
          width,
          height,
          parsed,
          cues,
          t: audio.currentTime,
          themeKey,
          title,
          logo,
          logoOpacity,
          fade: fadeLevelAt(endMark, audio.currentTime),
        });
  draw();

  const stream = canvas.captureStream(fps);
  dest.stream.getAudioTracks().forEach((track) => stream.addTrack(track));

  const recorder = new MediaRecorder(stream, {
    mimeType: mime,
    videoBitsPerSecond: width >= 1920 ? 8_000_000 : 5_000_000,
    audioBitsPerSecond: 192_000,
  });
  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size) chunks.push(e.data);
  };

  const total = stopAt !== null && songLength ? Math.min(stopAt, songLength) : stopAt ?? songLength;
  let raf = 0;
  let aborted = false;
  let fadeScheduled = false;
  let stopping = false;

  // Stops the recorder after a short tail so the last frame isn't cut.
  const finish = (tailMs) => {
    if (stopping) return;
    stopping = true;
    setTimeout(() => {
      try {
        audio.pause();
      } catch {
        /* already stopped */
      }
      if (recorder.state !== "inactive") recorder.stop();
    }, tailMs);
  };

  const cleanup = () => {
    cancelAnimationFrame(raf);
    try {
      audio.pause();
    } catch {
      /* already stopped */
    }
    stream.getTracks().forEach((track) => track.stop());
    actx.close().catch(() => {});
    URL.revokeObjectURL(audioUrl);
  };

  const finished = new Promise((resolve) => {
    recorder.onstop = () => resolve();
  });

  const loop = () => {
    const now = audio.currentTime;
    if (endMark !== null) {
      // Ramp the sound on the audio clock once, the moment the marker is
      // crossed: smooth, and exact even if a frame or two is dropped.
      // (A faded MP3 already fades its own sound; only the picture fades.)
      if (!fadeScheduled && now >= endMark && !fileFadesItself(songLength, stopAt)) {
        fadeScheduled = true;
        const left = Math.max(0.05, stopAt - now);
        const at = actx.currentTime;
        gain.gain.cancelScheduledValues(at);
        gain.gain.setValueAtTime(fadeLevelAt(endMark, now), at);
        gain.gain.linearRampToValueAtTime(0, at + left);
      }
      if (now >= stopAt) {
        draw(); // fully black
        if (onProgress && total) onProgress(1, total, total);
        finish(300);
        return;
      }
    }
    draw();
    if (onProgress && total) onProgress(Math.min(1, now / total), now, total);
    if (!audio.ended && !aborted) raf = requestAnimationFrame(loop);
  };

  if (signal) {
    signal.addEventListener(
      "abort",
      () => {
        aborted = true;
        if (recorder.state !== "inactive") recorder.stop();
      },
      { once: true }
    );
  }

  audio.addEventListener(
    "ended",
    () => {
      draw();
      // A short tail so the last frame is not cut mid-word.
      finish(400);
    },
    { once: true }
  );

  // Backup for the end marker: timeupdate still fires if drawing stalls.
  if (endMark !== null) {
    audio.addEventListener("timeupdate", () => {
      if (audio.currentTime >= stopAt + 0.25) finish(0);
    });
  }

  recorder.start(1000);
  await audio.play();
  raf = requestAnimationFrame(loop);

  await finished;
  cleanup();

  if (aborted) {
    throw new SingAlongExportError("aborted", "Recording stopped.");
  }

  const baseType = mime.split(";")[0];
  const blob = new Blob(chunks, { type: baseType });

  // 4. Never hand back a file that won't play. A grey video that uploads
  //    cleanly is worse than an error, because nobody finds out until a class
  //    presses play.
  if (onStatus) onStatus("Checking the finished video plays…");
  const check = await checkPlayable(blob, 15000);
  if (onStatus) onStatus("");
  if (!check.ok) {
    markBad(mime);
    const next = claimed()[0];
    throw new SingAlongExportError(
      "unplayable",
      `The recording came out as ${labelOf(recorder.mimeType || mime)}, but it won't play back here: ${check.why}. ` +
        "Nothing was saved. " +
        (next
          ? `Press Make video again — it will use ${labelOf(next)} instead.`
          : "This browser has no other format to try; use the latest Chrome or Edge.")
    );
  }

  return {
    blob,
    mime: baseType,
    // What the browser really used, e.g. "video/mp4;codecs=avc1.42E01E,mp4a.40.2".
    codec: recorder.mimeType || mime,
    codecLabel: labelOf(recorder.mimeType || mime),
    ext: baseType === "video/mp4" ? "mp4" : "webm",
    seconds: total,
    logoMissing,
  };
};

export default exportSingAlongVideo;
